import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { pickBy, isEqual, isEmpty } from 'lodash';
import {
  Box,
  Container,
  makeStyles,
  Typography,
  Button,
  Grid,
  List,
  Card
} from '@material-ui/core';
import { DeleteDialog, LoaderBar, Page } from 'src/components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router';
import { Alert } from '@material-ui/lab';
import RMAForm from './components/RMAForm';
import { usePermissions, useSnackBar } from 'src/hooks';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch } from 'src/redux';
import {
  allowedStatusThatRequiredBranch,
  rmaDetailsFields
} from 'src/redux/slices/rma/constants/rma-form-fields';
import {
  RMAField,
  RMAFieldLabel
} from 'src/redux/slices/rma/constants/rma-fields';
import {
  RmaAuditData,
  RMAData,
  RMAFormField,
  RMAImage,
  RMATransactionIncludedListing
} from 'src/redux/slices/rma/types';
import { getDynamicsAuditsThunk } from 'src/redux/slices/rma/thunks';
import { AuditHistory } from 'src/components/audit/AuditHistory';
import { generatePublicLink, snakeToTitleCase } from 'src/utils';
import useUserInfo from 'src/hooks/user/use-user-info';
import { RmaChangeSerialDropdown } from './components/RmaChangeSerialDropdown';
import { RMATransactionItem } from './components/RMATransactionItem';

const { actions: rmaActions } = slices.rma;

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100%',
    paddingBottom: 20,
    paddingTop: 20
  },
  contactCard: {
    marginTop: 20
  }
}));

interface FormValues {
  [key: string]: any; // could string, boolean, number, etc
}

const initialValues: FormValues = {
  // first_name: '',
  // last_name: '',
  // contact_no: '',
  // address: '',
  // source: '',
  // branch_released: '',
  // courier: '',
  // service_type: '',
  // total_amount: 0,
  // shipping_fee: 0,
  // is_shipping_fee_paid: false,
  // is_agree_marketing: false,
  // is_agree_tnc: false,
  // trial_os: '',
  // warranty_slip_no: '',
  // type_of_build: '',
  // with_upgrade: '',
  // kind_of_sales_transfer: '',
  // status: '',
  // build_tag: ''
};

const RMADetailsView = () => {
  const { id } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const snackBar = useSnackBar();
  const { canDeleteRMA, canUpdateRMA } = usePermissions();
  const { permissionNamesOnly, userBranches } = useUserInfo();

  const userBranchOptions = useMemo(
    () => userBranches?.map((item) => item?.branch_name) || [],
    [userBranches]
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [cleanRMAData, setCleanRMAData] = useState<RMAData>({});
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(
    false
  );
  const [auditsHistory, setAuditsHistory] = useState<RmaAuditData[]>([]);
  const [isAuditLoading, setIsAuditLoading] = useState<boolean>(false);
  const [isOpenChangeSerial, setIsOpenChangeSerial] = useState<boolean>(false);

  const fetchTapTapCustomerDetails = async () => {
    setIsLoading(true);

    if (id) {
      const getRmaRes = unwrapResult(
        await dispatch(rmaActions.getRmaViaIdThunk(Number(id)))
      );

      if (!getRmaRes?.success) {
        snackbar.show({
          severity: 'error',
          message: getRmaRes?.message || 'Error Getting Data'
        });
        console.error('errData', getRmaRes?.message || 'Error Getting Data');
        setIsLoading(false);
        return;
      }

      if (getRmaRes?.originalData?.data) {
        const rmaData: RMAData = getRmaRes?.originalData?.data;

        formik.setValues(rmaData);
        // Save this clean data to compare later if the server has made changes
        setCleanRMAData(rmaData);

        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (
    newValues: any,
    replace_serial_no?: string
  ) => {
    setIsLoading(true);

    if (cleanRMAData?.is_deleted) {
      setIsLoading(false);

      console.error('This RMA has been deleted');
      alert('This RMA has been deleted already');
      return;
    }

    const changedValues = pickBy(
      newValues,
      (value, key) => !isEqual(value, cleanRMAData[key])
    );

    if (isEmpty(changedValues) && !replace_serial_no) {
      setIsLoading(false);
      snackbar.show({
        severity: 'error',
        message: JSON.stringify('No changes detected')
      });
      return;
    }

    const rmaIncludedListingIds = (changedValues?.rma_included_listing || [])
      .filter(
        (item: RMATransactionIncludedListing) =>
          item?.listing_id !== cleanRMAData?.listing_id && item?.is_included
      )
      .map((item: RMATransactionIncludedListing) => item.listing_id)
      .filter(Boolean);

    // eslint-disable-next-line no-unused-vars
    const { rma_included_listing, ...filteredChangedValues } = changedValues;

    const updateRmaRes = unwrapResult(
      await dispatch(
        rmaActions.updateRmaThunk({
          ...filteredChangedValues,
          rma_id: Number(id),
          replace_serial_no: replace_serial_no,
          branch_name: allowedStatusThatRequiredBranch?.includes(
            newValues?.status
          )
            ? newValues?.branch
            : undefined,
          rma_transaction_include: rmaIncludedListingIds
        })
      )
    );

    if (!updateRmaRes?.success) {
      setIsLoading(false);

      const objKeysErrors = Object.keys(updateRmaRes?.originalData?.errors);
      const errorMsg = updateRmaRes?.originalData?.errors[objKeysErrors[0]];
      console.error('errData', updateRmaRes?.message || 'Error Updating Data');
      snackbar.show({
        severity: 'error',
        message:
          errorMsg ||
          JSON.stringify(updateRmaRes?.message || 'Error Updating Data')
      });
      return;
    }

    if (updateRmaRes?.success) {
      setIsLoading(false);

      // const response: TapTapCustomerData = data[0];

      if (updateRmaRes?.originalData?.data) {
        formik.setValues(updateRmaRes?.originalData?.data);
        setCleanRMAData(updateRmaRes?.originalData?.data);
        snackbar.show({
          severity: 'success',
          message: 'Customer updated successfully'
        });
      }

      return;
    }
  };

  const onDeleteCustomer = async () => {
    setIsDeleteDialogVisible(false);

    const deleteRmaRes = unwrapResult(
      await dispatch(rmaActions.rmaDeleteThunk(Number(id)))
    );

    console.log('deleteRmaRes', deleteRmaRes);

    if (!deleteRmaRes?.success) {
      snackbar.show({
        severity: 'error',
        message: deleteRmaRes?.message || 'Error Deleting Data'
      });
      console.error('errData', deleteRmaRes?.message || 'Error Deleting Data');
      return;
    }

    if (deleteRmaRes?.success) {
      snackbar.show({
        severity: 'success',
        message: 'RMA deleted successfully'
      });
      navigate('/app/rma');
      return;
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => handleFormSubmit(values),
    validationSchema: formSchema
  });

  const insertOtherOptionRmaField = useMemo((): RMAFormField[] => {
    const changedValues = pickBy(
      formik?.values,
      (value, key) => !isEqual(value, cleanRMAData[key])
    );

    const branchOptions =
      userBranchOptions?.map((data) => ({
        label: data,
        value: data,
        allowed_permission: []
      })) ?? [];

    if (
      allowedStatusThatRequiredBranch?.includes(formik?.values?.status) &&
      !isEmpty(changedValues)
    ) {
      const fields = [...rmaDetailsFields];

      const statusIndex = fields.findIndex(
        (field) => field.key === RMAField.STATUS
      );

      if (statusIndex !== -1) {
        fields.splice(statusIndex + 1, 0, {
          key: RMAField.BRANCH,
          type: 'autocomplete',
          label: RMAFieldLabel.BRANCH,
          options: branchOptions
        });
      }

      return fields;
    }

    return rmaDetailsFields;
  }, [formik.values, userBranchOptions, cleanRMAData]);

  const onDeleteImage = async (rma_transaction_image_id: number) => {
    console.log('onDeleteImage', rma_transaction_image_id);

    const deleteRmaImgsRes = unwrapResult(
      await dispatch(rmaActions.deleteRmaImageThunk(rma_transaction_image_id))
    );

    if (deleteRmaImgsRes?.success) {
      const newImgsForState = cleanRMAData?.images?.filter(
        (x: RMAImage) => x.rma_transaction_image_id !== rma_transaction_image_id
      );

      formik.setValues((prev) => ({ ...prev, images: newImgsForState }));
      setCleanRMAData((prev) => ({ ...prev, images: newImgsForState }));
    }

    console.log(deleteRmaImgsRes);
  };

  const onUploadImages = async (newlySelectedImages: RMAImage[]) => {
    const uploadRmaImgsRes = unwrapResult(
      await dispatch(
        rmaActions.uploadRmaImagesThunk({
          images: newlySelectedImages,
          rma_id: Number(id)
        })
      )
    );

    const newImgsForState = uploadRmaImgsRes?.originalData?.data?.images;

    formik.setValues((prev) => ({ ...prev, images: newImgsForState }));
    setCleanRMAData((prev) => ({ ...prev, images: newImgsForState }));
  };

  const onChangeImages = async (newImagesValue: RMAImage[]) => {
    const newlySelectedImages = newImagesValue.filter((x) => x?.file);
    if (newlySelectedImages.length > 0) {
      onUploadImages(newlySelectedImages);
      return;
    }

    // const oldImages = cleanRMAData?.images;

    const deletedElement = cleanRMAData?.images?.filter(
      (item: RMAImage) =>
        !newImagesValue.some(
          (newItem) =>
            newItem.rma_transaction_image_id === item.rma_transaction_image_id
        )
    );

    if (deletedElement?.length > 0) {
      onDeleteImage(deletedElement[0].rma_transaction_image_id);
    }
  };

  const onFormikFieldChange = (field: string, value: any) => {
    if (field === RMAField.RMA_IMAGES) {
      onChangeImages(value);
    }
  };

  const getRmaAudits = useCallback(async () => {
    if (!id) {
      console.error('Rma id missing');
      return;
    }
    setIsAuditLoading(true);
    // const res = unwrapResult(await dispatch(getRmaAuditsThunk(id)));
    const res = unwrapResult(
      await dispatch(
        getDynamicsAuditsThunk({
          auditable_id: id,
          auditable_type: 'RmaTransaction'
        })
      )
    );
    if (res.success) {
      const { data } = res.originalData;
      console.log(data, 'data');
      setAuditsHistory(data);
    } else {
      console.error('Failed at getting RMA Audits ');
    }
    setIsAuditLoading(false);
  }, [dispatch, id]);

  const generateLink = useCallback(
    async (serialNo: string, orderRefNo: string) => {
      if (!serialNo && !orderRefNo) {
        snackBar.show({
          severity: 'error',
          message:
            'Failed to generate link. Serial No. and Transaction No. are not available'
        });
        return;
      }

      await generatePublicLink(serialNo, orderRefNo);
      snackBar.show({
        severity: 'success',
        message: 'Link copied to clipboard'
      });
    },
    [snackBar]
  );

  const onHandleSelectIncludedListing = (
    rmaIncludedListing: RMATransactionIncludedListing,
    rmaTransaction: RMAData
  ) => {
    if (rmaIncludedListing.listing_id === rmaTransaction?.listing_id) return;

    const currentIncludedListing = rmaTransaction?.rma_included_listing;
    const newRmaTransactionInclude = currentIncludedListing?.map((item) => {
      if (item.listing_id === rmaIncludedListing?.listing_id) {
        return {
          ...item,
          is_included: !rmaIncludedListing?.is_included
        };
      }
      return item;
    });

    formik.setValues((prev) => ({
      ...prev,
      rma_included_listing: newRmaTransactionInclude
    }));
  };

  useEffect(() => {
    fetchTapTapCustomerDetails();
    getRmaAudits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page className={classes.root} title="RMA Details">
      <Container maxWidth={false} style={{ opacity: isLoading ? 0.3 : 1 }}>
        {formik?.values?.is_deleted && (
          <Alert
            hidden
            severity="error"
            style={{ marginTop: 10, marginBottom: 10 }}
          >
            <Typography variant="h3">
              RMA IS ALREADY DELETED. YOU CANNOT EDIT THIS RMA ANYMORE.
            </Typography>
          </Alert>
        )}
        <LoaderBar isLoading={isLoading} />
        <Box>
          <Typography variant="h3">
            RMA Details - {`${formik?.values?.customer_name}`}
            {(formik?.values?.serial_no || formik?.values?.transaction_no) && (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                style={{ marginLeft: 20 }}
                onClick={() =>
                  generateLink(
                    formik?.values?.serial_no,
                    formik?.values?.transaction_no
                  )
                }
              >
                Generate Public Link
              </Button>
            )}
          </Typography>

          <Typography variant="h6">
            By:{' '}
            {`${formik?.values?.created_by} on ${formik?.values?.created_at}`}
          </Typography>

          <Typography variant="h6">
            Time Spent: {formik?.values?.turnaround_time}
          </Typography>
        </Box>

        {formik?.values?.rma_included_listing?.length > 0 ? (
          <Card style={{ padding: 10, marginTop: 10 }}>
            <Box>
              <Typography variant="h4">RMA Included Listings</Typography>
            </Box>
            <List>
              {formik?.values?.rma_included_listing?.map((item: any) => (
                <RMATransactionItem
                  key={item?.id}
                  selectedRmaId={formik?.values?.listing_id || undefined}
                  transactionItem={item}
                  onSelectTransactionItem={(
                    rmaIncluded: RMATransactionIncludedListing
                  ) =>
                    onHandleSelectIncludedListing(rmaIncluded, formik.values)
                  }
                  included_serial={formik?.values?.rma_transaction_include}
                />
              ))}
            </List>
          </Card>
        ) : null}

        <Box mt={2}>
          <RMAForm
            formikHookRef={formik}
            formFields={insertOtherOptionRmaField || []}
            rmaPermission={permissionNamesOnly}
            onChange={onFormikFieldChange}
            isCreatepage={false}
          />
        </Box>

        <AuditHistory
          summary={`View Update/Edit History For Serial No.${formik.values.serial_no}`}
          auditData={auditsHistory ?? []}
          isLoading={isAuditLoading}
          moduleName="RMA"
          formatValue={(key, value) => {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'number' || typeof value === 'boolean') {
              return value.toString();
            }
            if (typeof value === 'object') {
              return JSON.stringify(value);
            }
            return snakeToTitleCase(value);
          }}
        />

        <Grid
          container
          style={{ marginTop: 20, justifyContent: 'space-between' }}
        >
          {canDeleteRMA ? (
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => setIsDeleteDialogVisible(true)}
              >
                Delete RMA
              </Button>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={4} container spacing={2}>
            <Grid item xs={12} sm>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setIsOpenChangeSerial(true)}
              >
                Change Serial
              </Button>
            </Grid>
            {canUpdateRMA ? (
              <Grid item xs={12} sm>
                <Button
                  fullWidth
                  onClick={async () => {
                    const validateForm = await formik.validateForm();
                    if (validateForm && Object.keys(validateForm).length > 0) {
                      const errorFields = Object.keys(validateForm).join(', ');
                      const message = `Please check the following fields: ${errorFields}`;
                      snackbar.show({
                        severity: 'error',
                        message,
                        useSound: true
                      });
                      return;
                    }

                    formik.handleSubmit();
                  }}
                  color="primary"
                  variant="contained"
                  disabled={isLoading}
                >
                  Save RMA
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Container>

      <RmaChangeSerialDropdown
        open={isOpenChangeSerial}
        onClose={() => {
          setIsOpenChangeSerial(false);
        }}
        onSubmit={(serial_no?: string) =>
          handleFormSubmit(formik?.values, serial_no)
        }
      />

      <DeleteDialog
        title="Delete RMA"
        subTitle={`Are you sure you want to delete this RMA?`}
        isVisible={isDeleteDialogVisible}
        handleClose={() => setIsDeleteDialogVisible(false)}
        onDelete={onDeleteCustomer}
      />
      {/* <DecisionDialog
        isOpen={amountChangedDialog}
        title="Total Amount Has Changed"
        subTitle={totalChangedDialogCopy}
        onHandleConfirmAction={() => theTotalAmountHasChangedThusUpdateIt()}
        onHandleClose={() => setAmountChangedDialog(false)}
      /> */}
    </Page>
  );
};

export default RMADetailsView;

const formSchema = yup.object().shape({
  [RMAField.CUSTOMER_NAME]: yup.string().required('Customer Name is required'),
  [RMAField.LOCATION]: yup.string().required('Location is required'),
  [RMAField.STATUS]: yup.string().required('Status is required'),
  [RMAField.REPORTED_PROBLEM]: yup
    .string()
    .required('Reported Problem is required'),
  [RMAField.CONTACT_NO]: yup
    .string()
    .nullable()
    .matches(/\d+$/, 'Contact number must be numeric')
});
